// ── Drag session start ──
// The phases a furniture pointerdown goes through BEFORE any handler is attached, in the
// order the session runs them:
//
//   resolveDragTarget()        can this pointerdown start a drag at all, and what are the
//                              live (not closure-stale) values of the fields it needs?
//   collectDragScope()         the per-drag tile maps + the items riding on the dragged one
//   captureSurfaceOrigin()     where the item started, so the drop can tell "moved within a
//                              surface" from "dropped onto a surface"
//   bootstrapWindowSnap()      the wall a wall-mounted item is already on, so hysteresis
//                              starts correct on frame 0
//
// Every body below is byte-identical to the original inline code in beginFurnitureDragSession;
// the only change is that closure variables arrive through the session context object.
import * as PIXI from 'pixi.js';
import type { Viewport } from 'pixi-viewport';
import {
  isFullyOnSurface,
  getFurnitureDepthComponents,
  getOccupiedBaseTile,
  computeSurfaceTileMap,
  computeFootprintTileMap,
  type SurfaceTileMap,
} from '../../../domain/depthMath';
import { collectCarriedItems, type CarriedItem } from './surfaceCarry';
import { toPlacementMode, isSurface } from './placement';
import type { RenderFurniture } from '../../../domain/types';
import type { DragSessionContext } from './dragSession';
import { asFurnitureSprite } from '../render/pixiExtensions';
import {
  snapWindowAnchorToGrid as snapWindowAnchorToGridImported,
  WINDOW_VERTICAL_OFFSET,
  type WindowSnapResult,
} from '../../../domain/windowSnap';

/**
 * What the session needs to know about the item being grabbed, read at pointerdown.
 * The `live*` fields deliberately come from the sprite rather than the captured
 * `furniture` object: that closure was built when the sprite was created and does NOT
 * see later `queryClient.setQueryData` writes, while the sprite's `furnitureData` is
 * re-synced on every render.
 */
export interface DragTarget {
  roomContainer: PIXI.Container;
  roomViewport: Viewport | null;
  livePlacementMode: RenderFurniture['placement_mode'];
  liveWallFloorOffset: number;
}

/** The per-drag maps and the items that must travel with the dragged one. */
export interface DragScope {
  dragSurfaceMap: SurfaceTileMap;
  dragFootprintMap: Map<string, number>;
  dragSurfaceItemFootprintMap: Map<string, number>;
  carriedItems: CarriedItem[];
  carriedSet: Set<number>;
}

/** Where the dragged item sat when the gesture began. */
export interface DragSurfaceOrigin {
  startIsInSurfaceArea: boolean;
  startTileX: number | null;
  startTileY: number | null;
}

/**
 * Runs the gates and reads the live fields. `null` = this pointerdown is not ours; the
 * session must return without touching anything else.
 */
export function resolveDragTarget(
  event: PIXI.FederatedPointerEvent,
  ctx: DragSessionContext,
): DragTarget | null {
  const { scene: { getRoomContainer, getRoomViewport }, refs: { drag, admin }, item: { sprite, furniture, markerContainer } } = ctx;

  // roomContainer/roomViewport are module-level mutable scene state owned by
  // RoomCanvas — read live values via getters at handler-invocation time
  // (the original closure read the module variables at the same moment).
  const roomContainer = getRoomContainer();
  const roomViewport = getRoomViewport();
  if (!roomContainer) return null;
  // Block furniture interaction while admin tile editor is open
  if (admin.tileMode !== null) return null;
  if (event.pointerType === 'mouse' && event.button !== 0) return null;

  // Signal to the viewport pointerdown handler that a furniture sprite was hit,
  // so it doesn't clear the green selection outline on this event.
  drag.furnitureJustTapped = true;

  // The closure 'furniture' variable may be stale — it was captured when the sprite was
  // first created and won't update when queryClient.setQueryData changes placement_mode.
  // The sprite's furnitureData is kept in sync on every re-render, so read
  // the live value from there for any field that can change between drags.
  const liveFD = asFurnitureSprite(sprite)?.furnitureData;
  const livePlacementMode = liveFD?.placement_mode
    ?? furniture.placement_mode;
  // wall_floor_offset: per-instance first, then catalog fallback.
  const liveWallFloorOffset = liveFD?.wall_floor_offset
    ?? furniture.wall_floor_offset
    ?? liveFD?.furniture_catalog?.wall_floor_offset
    ?? furniture.furniture_catalog?.wall_floor_offset ?? 0;

  // Check if marker container still exists and is valid before starting drag
  if (!markerContainer || !markerContainer.position) {
    // console.warn(`[RoomCanvas] Cannot start drag - markerContainer or markerContainer.position is null for furniture ${furniture.id}`);
    return null;
  }

  return { roomContainer, roomViewport, livePlacementMode, liveWallFloorOffset };
}

/**
 * Builds the tile maps once per drag (not per frame) and discovers the carried items.
 * Carried items travel WITH the dragged item, so their original footprint tiles are no
 * longer occupied — they are pruned from both footprint maps here, or every frame would
 * report a conflict against the item's own passengers.
 */
export function collectDragScope(ctx: DragSessionContext): DragScope {
  const { refs: { furniture: furnitureScene, drag }, item: { furniture, markerContainer } } = ctx;

  // Build surface tile map at drag start (once per drag, not per frame).
  // This map is used by snapAnchorToGrid (extended bounds) and onPointerMove/Up (z-index).
  const dragSurfaceMap = computeSurfaceTileMap(furnitureScene.markers, furniture.id);
  const dragFootprintMap = computeFootprintTileMap(furnitureScene.markers, furniture.id);
  const dragSurfaceItemFootprintMap = computeFootprintTileMap(furnitureScene.markers, furniture.id, true);

  // Discover items that must move with the drag (fixed-point over combined
  // surface maps — see room/surfaceCarry.ts for the full rationale).
  const { carriedItems, carriedSet } = collectCarriedItems(
    furnitureScene.markers, furniture.id, markerContainer,
  );

  // Track carried items so the furniture render useEffect doesn't reset their PIXI positions
  drag.draggingCarriedIds = new Set(carriedItems.map(c => c.furnitureId));

  for (const [key, fId] of [...dragFootprintMap]) {
    if (carriedSet.has(fId)) dragFootprintMap.delete(key);
  }
  for (const [key, fId] of [...dragSurfaceItemFootprintMap]) {
    if (carriedSet.has(fId)) dragSurfaceItemFootprintMap.delete(key);
  }

  return { dragSurfaceMap, dragFootprintMap, dragSurfaceItemFootprintMap, carriedItems, carriedSet };
}

/**
 * Records whether this floor item ALREADY sits inside a surface's tile area. If it does,
 * dropping it back on the SAME TILE must not reclassify it as 'surface' — otherwise a
 * floor item that merely got nudged near the head of a bed silently climbs onto it.
 * Moving to a DIFFERENT tile within the surface IS intentional and still reclassifies.
 */
export function captureSurfaceOrigin(
  ctx: DragSessionContext,
  livePlacementMode: RenderFurniture['placement_mode'],
  dragSurfaceMap: SurfaceTileMap,
): DragSurfaceOrigin {
  const { item: { sprite, markerContainer }, geometry: { tileDimW, tileDimH, interactionIsRug, getLiveCatalog } } = ctx;

  let startIsInSurfaceArea = false;
  let startTileX: number | null = null;
  let startTileY: number | null = null;
  if (!isSurface(toPlacementMode(livePlacementMode)) && dragSurfaceMap.size > 0) {
    const depthStart = getFurnitureDepthComponents(markerContainer, tileDimW, tileDimH, interactionIsRug);
    const mirStart = sprite.scale.x < 0;
    const baseStart = getOccupiedBaseTile(depthStart, mirStart);
    const fpStart = getLiveCatalog()?.footprint_tiles;
    const tileCountStart = fpStart?.length ?? (depthStart.effectiveWidthTiles * depthStart.effectiveHeightTiles);
    startTileX = Math.round(depthStart.tileX);
    startTileY = Math.round(depthStart.tileY);
    startIsInSurfaceArea = isFullyOnSurface(
      fpStart, startTileX, startTileY,
      baseStart.rawBaseTileX, baseStart.rawBaseTileY,
      baseStart.overlayWidthTiles, baseStart.overlayHeightTiles,
      dragSurfaceMap, undefined, tileCountStart, mirStart,
    );
  }
  return { startIsInSurfaceArea, startTileX, startTileY };
}

/**
 * For wall-mounted items: bootstraps previousWindowSnap so hysteresis starts on the
 * correct wall from the very first drag frame. Without this, frame 0 has no previousSnap
 * and picks the wall purely from cursorWorldX — unreliable, because the user may click on
 * the RIGHT part of a LEFT-wall item (especially near the corner, where the item's worldX
 * can be positive even though it hangs on the left wall).
 *
 * Strategy: sprite.scale.x is the ground truth for the current wall (set when the item was
 * last placed, always correct); baseTile values come from an anchor-based snap with no
 * cursor bias. This avoids a left-wall picture behind a cabinet flipping 180° because the
 * click landed at positive X.
 */
export function bootstrapWindowSnap(
  ctx: DragSessionContext,
  liveWallFloorOffset: number,
): WindowSnapResult | undefined {
  const {
    item: { sprite, markerContainer },
    geometry: { isWindow, rawEffectiveWidthTiles, rawEffectiveHeightTiles },
  } = ctx;

  if (!isWindow) return undefined;

  const currentlyMirrored = sprite.scale.x < 0;
  // Compute anchor-based snap (no cursorWorldX) for baseTile coordinates.
  const anchorSnap = snapWindowAnchorToGridImported(
    markerContainer.position.x,
    markerContainer.position.y - WINDOW_VERTICAL_OFFSET - liveWallFloorOffset,
    rawEffectiveWidthTiles,
    rawEffectiveHeightTiles,
  );
  // Override shouldMirror with sprite state (reliable) and normalise baseTile to that wall.
  return {
    ...anchorSnap,
    shouldMirror: currentlyMirrored,
    baseTileX: currentlyMirrored ? 0 : Math.max(1, anchorSnap.baseTileX),
    baseTileY: currentlyMirrored ? Math.max(1, anchorSnap.baseTileY) : 0,
    isOnWall: true,
  };
}
